import { Drawer } from 'antd'
import styled from 'styled-components'
import { Text, Heading } from '@lmc/ui-kit/src/general'
import { Spinner, Avatar, FormCard } from '@lmc/ui-kit'
import { useTranslation } from 'react-i18next'
import { NAMESPACE } from 'locale'
import { Tag } from 'antd/lib'
import dayjs from 'dayjs'
import { StockRecord } from '../../types'
import Discontinued from '../../iconsV2/Discontinued'

type DiscontinuedRecordDrawerProps = {
  isVisible: boolean
  onClose: () => void
  record: StockRecord | undefined
  medicationName: string
  controlledDrug?: boolean
}

export const DiscontinuedRecordDrawer = ({
  isVisible,
  onClose,
  record,
  medicationName,
  controlledDrug
}: DiscontinuedRecordDrawerProps) => {
  const { t } = useTranslation(NAMESPACE.EMAR_V2)
  const isLoading = false

  if (!record) return null

  const reasonForChange = Array.isArray(record.notes)
    ? record.notes.join(', ')
    : record.notes || ''

  const actionedDate = record.actionTime || record.date
  const actionedDateFormatted = actionedDate
    ? dayjs(actionedDate).format('DD-MM-YYYY HH:mm')
    : '-'

  const actionedByName =
    typeof record.recordedBy?.name === 'object'
      ? `${record.recordedBy?.name?.first || ''} ${record.recordedBy?.name?.last || ''}`.trim()
      : (record.recordedBy?.name as unknown as string) || ''

  return (
    <StyledDrawer
      title={
        <>
          {isLoading && (
            <LoadingWrapper>
              <Spinner />
            </LoadingWrapper>
          )}
          {!isLoading && record && (
            <TitleWrapper>
              <MedicationNameRow>
                <MedicationNameText>{medicationName}</MedicationNameText>
                {controlledDrug && <Tag color="red">{t('CD')}</Tag>}
              </MedicationNameRow>
              <TitleContainer>
                <IconWrapper>
                  <Discontinued />
                </IconWrapper>
                <StyledTitleHeading level={4}>
                  {t('MEDICATION_DISCONTINUED')}
                </StyledTitleHeading>
              </TitleContainer>
            </TitleWrapper>
          )}
        </>
      }
      placement="right"
      onClose={onClose}
      open={isVisible}
      width={736}
    >
      {isLoading && (
        <LoadingWrapper>
          <Spinner />
        </LoadingWrapper>
      )}
      {!isLoading && record && (
        <DrawerContent>
          <TopContent>
            <DetailsHeading level={3}>{t('DETAILS')}</DetailsHeading>
          </TopContent>
          <FormCard level={2}>
            <InfoRow>
              <FullWidthSection>
                <StyledLabel level={2}>{t('REASON_FOR_CHANGE')}</StyledLabel>
                <Text>{reasonForChange || '-'}</Text>
              </FullWidthSection>
            </InfoRow>
            <RowDivider />
            <InfoRow>
              <InfoSection>
                <StyledLabel level={2}>{t('ACTIONED_DATE')}</StyledLabel>
                <Text>{actionedDateFormatted}</Text>
              </InfoSection>
              <InfoSection>
                <StyledLabel level={2}>{t('ACTIONED_BY')}</StyledLabel>
                <ActionedByContainer>
                  <Avatar
                    url={record.recordedBy?.picture as unknown as string}
                    size={24}
                  />
                  <Text>{actionedByName || '-'}</Text>
                </ActionedByContainer>
              </InfoSection>
            </InfoRow>
          </FormCard>
        </DrawerContent>
      )}
    </StyledDrawer>
  )
}

const StyledDrawer = styled(Drawer)`
  .ant-drawer-header {
    box-shadow: 0px 2px 4px 0px rgba(0, 0, 0, 0.1);
  }

  .ant-drawer-header-title {
    flex-direction: row-reverse;
  }
`

const TitleWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`

const TitleContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`

const IconWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;

  svg {
    width: 20px;
    height: 20px;
  }
`

const StyledTitleHeading = styled(Heading)`
  color: ${(props) => props.theme.color.neutral.color3};
`

const LoadingWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100%;
  width: 100%;
`

const DrawerContent = styled.div``

const MedicationNameRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`

const MedicationNameText = styled(Text)`
  color: ${(props) => props.theme.color.neutral.color5};
`

const TopContent = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  padding: 0 0 16px 0;
`

const DetailsHeading = styled(Heading)`
  color: ${({ theme }) => theme.color.neutral.color3};
  margin: 0;
`

const InfoRow = styled.div`
  display: flex;
  flex-direction: row;
  gap: 24px;
`

const InfoSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  flex: 1;
`

const FullWidthSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  width: 100%;
`

const StyledLabel = styled(Text)`
  color: ${({ theme }) => theme.color.neutral.color5};
`

const ActionedByContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`

const RowDivider = styled.div`
  height: 1px;
  background-color: ${({ theme }) => theme.color.neutral.color9};
  margin: 16px 0;
`
